import {
    ContentCellData,
    CellTextContentData,
    CellExpanderContentData,
    CellHyperlinkContentData,
    TextContent
} from "@/libraries/design/patterns/table/src/cell-variants/types";

import {
    ContentfulFeature,
    FeatureAdditionalInformation,
    FeatureData,
    RelevantPlan
} from "./pricing-table";

export const extractSectionTitle = (featureGroup: ContentfulFeature) => {
    const { fields } = featureGroup;
    return fields.featureGroupTitleExternal;
};

export const extractFeatureName = (featureFields: FeatureData["fields"]) => {
    return featureFields.featureNameExternal || "";
};

export const createPlanCells = (
    featureFields: FeatureData["fields"],
    planTitles: string[]
) => {
    // @ts-ignore
    return planTitles.map((planTitle, i) => {
        const isRelevant = featureFields.relevantPlan?.some(
            (plan: RelevantPlan) => plan.fields?.planTitleExternal === planTitle
        );

        const additionalInfo =
            // @ts-ignore
            featureFields?.featureAdditionalInformation?.find<FeatureAdditionalInformation>(
                // @ts-ignore
                (info: FeatureAdditionalInformation) => {
                    return !!info.fields?.relevantPlan?.some(
                        (plan: RelevantPlan) =>
                            plan.fields?.planTitleExternal === planTitle
                    );
                }
            )?.fields.featureAdditionalInformation;

        // 1. if there is featureAdditionalInformation, use that as type rich text (links, etc.)
        if (additionalInfo) {
            return {
                type: "text" as const,
                check: additionalInfo && isRelevant,
                value: {
                    type: "richtext" as const,
                    value: additionalInfo
                }
            };
        }

        // 2. if isRelevant, use type check with empty value
        if (isRelevant) {
            return {
                type: "check"
            };
        }

        // 3. otherwise, use type text with empty value
        return {
            type: "text" as const,
            value: {
                type: "plaintext" as const,
                value: ""
            }
        };
    });
};

// for the left side column of the table
export const createFeatureCell = (
    featureFields: FeatureData["fields"],
    featureName: string,
    descriptionText?: string
): ContentCellData => {
    if (featureFields.relevantUrl) {
        return {
            type: "hyperlink",
            label: featureName,
            href: featureFields.relevantUrl
        } as CellHyperlinkContentData;
    }
    if (descriptionText) {
        return {
            type: "expander",
            label: featureName,
            lozenge: featureFields.callOut,
            body: {
                type: "richtext",
                value: descriptionText || featureName
            } as TextContent
        } as CellExpanderContentData;
    }

    return {
        type: "text",
        value: {
            type: "plaintext",
            value: featureName
        },
        lozenge: featureFields.callOut,
        check: false
    } as CellTextContentData;
};

export const transformFeatureToRow = (
    feature: FeatureData,
    planTitles: string[]
) => {
    const featureFields = feature.fields;

    // left side column link or label
    const featureName = extractFeatureName(featureFields);
    // individual cells
    const planCells = createPlanCells(featureFields, planTitles);

    // left side column
    const featureCell = createFeatureCell(
        featureFields,
        featureName,
        featureFields.description
    );

    return [featureCell, ...planCells];
};

export const transformFeatureGroupToSection = (
    featureGroup: ContentfulFeature,
    planTitles: string[]
) => {
    const sectionTitle = extractSectionTitle(featureGroup);
    const rows =
        featureGroup.fields.features?.map((feature) =>
            transformFeatureToRow(feature, planTitles)
        ) || [];

    return {
        title: sectionTitle,
        rows
    };
};

export const transformContentfulDataToTableData = (
    contentfulData: ContentfulFeature[],
    planTitles: string[]
) => {
    return contentfulData.map((featureGroup) =>
        transformFeatureGroupToSection(featureGroup, planTitles)
    );
};
